/**
 * NatureAtlasCategory — /nature/atlas/:category
 *
 * Species / places grid for one category. Includes:
 *   • Live search across atlas
 *   • Explore button (also triggers fuzzy suggest)
 *   • Empty-state fallback: fuzzy suggestion + "Request full profile"
 *     button that queues an AI-enriched entry.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Camera, Sparkles, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";

export default function NatureAtlasCategory() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState({ total: 0, items: [] });
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [suggest, setSuggest] = useState({ items: [], reason: "" });
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const m = await http.get("/nature/atlas/meta").then(r => r.data);
        setMeta(m);
      } catch { /* ignore */ }
    })();
  }, []);

  // debounce query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const url = debouncedQ
          ? `/nature/atlas/entities?category=${category}&q=${encodeURIComponent(debouncedQ)}&limit=48`
          : `/nature/atlas/entities?category=${category}&limit=48`;
        const r = await http.get(url).then(r => r.data);
        if (alive) setData(r);
      } catch {
        if (alive) setData({ total: 0, items: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [category, debouncedQ]);

  // Fetch spell-check suggestions when the atlas returns zero results
  useEffect(() => {
    if (!debouncedQ || (data.items || []).length > 0) {
      setSuggest({ items: [], reason: "" });
      return;
    }
    let alive = true;
    http.get(`/nature/atlas/suggest?q=${encodeURIComponent(debouncedQ)}&category=${category}`)
      .then(r => alive && setSuggest(r.data || { items: [], reason: "" }))
      .catch(() => alive && setSuggest({ items: [], reason: "no-match" }));
    return () => { alive = false; };
  }, [debouncedQ, data.items, category]);

  const catDef = meta?.groups?.flatMap(g => g.categories)?.find(c => c.slug === category);

  const triggerExplore = () => {
    // Force refresh with current query — mostly for the Explore button.
    setDebouncedQ(q.trim());
  };

  const requestEntry = async () => {
    if (!q.trim()) return;
    setRequesting(true);
    try {
      const r = await http.post("/nature/atlas/request-entry", {
        name: q.trim(),
        category,
      });
      const slug = r.data?.slug;
      toast.success(r.data?.created ? "Profile queued — building now" : "This profile already exists");
      if (slug) navigate(`/nature/species/${slug}`);
    } catch (e) {
      toast.error("Could not queue this entry. Please try again.");
    } finally {
      setRequesting(false);
    }
  };

  const emptyAfterSearch = !loading && debouncedQ && (data.items || []).length === 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid={`nature-cat-page-${category}`}>
      <Link to="/nature" className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Nature hub
      </Link>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
            {catDef?.kind?.toUpperCase() || "CATEGORY"}
          </div>
          <h1 className="font-display text-3xl md:text-4xl">
            {catDef?.label || category}
          </h1>
        </div>
        <Link to="/nature/identify" className="wofy-btn-primary" data-testid="nature-cat-identify">
          <Camera className="w-4 h-4" /> Identify with AI
        </Link>
      </div>

      {/* Search row with Explore button */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && triggerExplore()}
            placeholder={`Search ${catDef?.label || category}…`}
            className="w-full pl-9 pr-3 py-2 rounded-lg border bg-transparent text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="nature-cat-search"
          />
        </div>
        <button
          type="button"
          onClick={triggerExplore}
          className="text-sm font-mono uppercase tracking-wider rounded-lg px-3 py-2 border transition-colors hover:bg-black/5"
          style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-primary)" }}
          data-testid="nature-cat-explore-btn"
        >
          Explore &gt;
        </button>
      </div>

      {loading && (
        <div className="text-sm opacity-70 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      )}

      {/* Empty state with fuzzy suggest + request-entry CTA */}
      {emptyAfterSearch && (
        <div
          className="rounded-2xl border p-6 text-center max-w-xl mx-auto"
          style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
          data-testid="nature-cat-empty"
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
               style={{ background: "#fef3c7", color: "#b45309" }}>
            <AlertCircle className="w-6 h-6" />
          </div>
          <div className="font-display text-xl leading-snug">
            Nothing matches “{debouncedQ}” yet
          </div>
          <p className="text-sm opacity-70 mt-1 max-w-md mx-auto">
            No results in <strong>{catDef?.label || category}</strong>. We can build a full profile for
            you — Wikipedia bio, photos, sourced links, and a discussion box —
            in about a minute.
          </p>

          {/* Fuzzy suggestions */}
          {(suggest.items || []).length > 0 && (
            <div className="mt-4">
              <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
                Did you mean…
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {suggest.items.slice(0, 6).map((s) => (
                  <Link
                    key={s.slug}
                    to={`/nature/species/${s.slug}`}
                    className="text-sm rounded-full border px-3 py-1 hover:bg-black/5"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid={`nature-cat-suggest-${s.slug}`}
                  >
                    {s.name || s.common_name || s.slug}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={requestEntry}
            disabled={requesting}
            className="wofy-btn-primary mt-5 mx-auto"
            data-testid="nature-cat-request-entry"
          >
            {requesting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Queuing your request…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" /> Build me a “{debouncedQ}” profile <ArrowRight className="w-3 h-3" />
              </>
            )}
          </button>
        </div>
      )}

      {/* Genuine empty on load with no query */}
      {!loading && !debouncedQ && (data.items || []).length === 0 && (
        <div className="wofy-card !p-6 opacity-80 text-center">Nothing here yet.</div>
      )}

      {/* Results grid */}
      {!loading && (data.items || []).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="nature-cat-grid">
          {data.items.map((it) => (
            <Link
              key={it.slug}
              to={`/nature/species/${it.slug}`}
              data-testid={`nature-species-${it.slug}`}
              className="rounded-xl border p-4 hover:bg-black/5 transition"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              <div className="font-medium leading-tight">{it.name}</div>
              {it.scientific_name && (
                <div className="text-[11px] font-mono italic opacity-70 mt-0.5">{it.scientific_name}</div>
              )}
              {it.tagline && <p className="text-xs opacity-80 mt-1 line-clamp-2">{it.tagline}</p>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
